import { useState, useEffect } from 'react';
import { Card, Table, Empty, Spin, Alert, Tag, Button, Typography, Image, Tooltip, Modal, Input, message } from 'antd';
import { PlusOutlined, BugOutlined, BulbOutlined, LinkOutlined, PictureOutlined, CheckCircleOutlined, ClockCircleOutlined, ReloadOutlined, UserOutlined, TeamOutlined, EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/useTheme';
import feedbackService, { type Feedback, type FeedbackStatus, type FeedbackType } from '../../api/feedbackService';
import { FeedbackModal } from '../../components/Feedback';
import { API_BASE_URL } from '../../utils/constants';
import { useAuth } from '../../hooks/useAuth';
import type { ColumnsType } from 'antd/es/table';

const { TextArea } = Input;

const { Text, Paragraph } = Typography;

const statusColors: Record<FeedbackStatus, string> = {
  open: 'blue',
  reviewed: 'purple',
  planned: 'cyan',
  closed: 'default',
};

const statusLabels: Record<FeedbackStatus, string> = {
  open: 'Open',
  reviewed: 'Reviewed',
  planned: 'Planned',
  closed: 'Closed',
};

const typeIcons: Record<FeedbackType, React.ReactNode> = {
  bug: <BugOutlined style={{ color: '#f5222d' }} />,
  feature: <BulbOutlined style={{ color: '#faad14' }} />,
};

const typeLabels: Record<FeedbackType, string> = {
  bug: 'Bug Report',
  feature: 'Feature Request',
};

export const FeedbackPage = () => {
  const { theme } = useTheme();
  const { authState } = useAuth();
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [screenshotModalOpen, setScreenshotModalOpen] = useState(false);
  const [selectedScreenshotId, setSelectedScreenshotId] = useState<string | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState<string>('');
  const [savingDescription, setSavingDescription] = useState(false);

  // Status filter state - default excludes 'closed'
  const [statusFilters, setStatusFilters] = useState<Set<FeedbackStatus>>(
    new Set(['open', 'reviewed', 'planned'])
  );

  // Toggle status filter
  const toggleStatusFilter = (status: FeedbackStatus) => {
    setStatusFilters(prev => {
      const newFilters = new Set(prev);
      if (newFilters.has(status)) {
        newFilters.delete(status);
      } else {
        newFilters.add(status);
      }
      return newFilters;
    });
  };

  // Filter feedback based on selected statuses
  const filteredFeedback = feedback.filter(f => statusFilters.has(f.status));

  // Get row style based on status for better UX - matching tag colors exactly
  const getRowStyle = (record: Feedback): React.CSSProperties => {
    const isDark = theme === 'dark';
    const status = record.status;
    
    switch (status) {
      case 'open':
        // Ant Design blue: #1890ff
        return {
          backgroundColor: isDark ? 'rgba(24, 144, 255, 0.12)' : 'rgba(24, 144, 255, 0.15)',
        };
      case 'reviewed':
        // Ant Design purple: #722ed1
        return {
          backgroundColor: isDark ? 'rgba(114, 46, 209, 0.12)' : 'rgba(114, 46, 209, 0.15)',
        };
      case 'planned':
        // Ant Design cyan: #13c2c2
        return {
          backgroundColor: isDark ? 'rgba(19, 194, 194, 0.12)' : 'rgba(19, 194, 194, 0.15)',
        };
      case 'closed':
        // Ant Design default gray: #d9d9d9
        return {
          backgroundColor: isDark ? 'rgba(217, 217, 217, 0.08)' : 'rgba(217, 217, 217, 0.08)',
        };
      default:
        return {};
    }
  };

  // Build screenshot URL with auth token
  // Uses dedicated endpoint that handles its own auth (bypasses general middleware)
  const getScreenshotUrl = (feedbackId: string) => {
    return `${API_BASE_URL}/api/feedback-screenshot/${feedbackId}?token=${authState.token}`;
  };

  const fetchFeedback = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await feedbackService.getMyFeedback();
      if (response.success && response.data) {
        setFeedback(response.data);
      } else {
        setError('Failed to load feedback');
      }
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError(err instanceof Error ? err.message : 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback();
  }, []);

  const handleFeedbackModalClose = () => {
    setFeedbackModalOpen(false);
    // Refresh the list after submitting new feedback
    fetchFeedback();
  };

  const handleStartEdit = (record: Feedback) => {
    setEditingId(record.id);
    setEditingDescription(record.description);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingDescription('');
  };

  const handleSaveDescription = async (feedbackId: string) => {
    if (!editingDescription.trim()) {
      message.error('Description cannot be empty');
      return;
    }

    setSavingDescription(true);
    try {
      const result = await feedbackService.updateFeedbackDescription(feedbackId, editingDescription.trim());
      if (result.success) {
        message.success('Description updated successfully');
        // Update local state
        setFeedback(prev =>
          prev.map(f => f.id === feedbackId
            ? { ...f, description: editingDescription.trim() }
            : f
          )
        );
        setEditingId(null);
        setEditingDescription('');
      } else {
        message.error('Failed to update description');
      }
    } catch (err) {
      console.error('Error updating description:', err);
      message.error('Failed to update description');
    } finally {
      setSavingDescription(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns: ColumnsType<Feedback> = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type: FeedbackType) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {typeIcons[type]}
          <span>{typeLabels[type]}</span>
        </span>
      ),
      filters: [
        { text: 'Bug Report', value: 'bug' },
        { text: 'Feature Request', value: 'feature' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
      render: (title: string) => (
        <Text strong>{title}</Text>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => (
        <Text 
          type="secondary" 
          style={{ 
            fontSize: 13,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {description}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: FeedbackStatus) => (
        <Tag color={statusColors[status]}>
          {statusLabels[status]}
        </Tag>
      ),
    },
    {
      title: 'Submitted',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => (
        <Text type="secondary">{formatDate(date)}</Text>
      ),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Response',
      key: 'response',
      width: 100,
      align: 'center',
      render: (_, record) => (
        record.response ? (
          <Tooltip title={`Responded by ${record.response_by}`}>
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Replied
            </Tag>
          </Tooltip>
        ) : (
          <Tooltip title="Awaiting response">
            <Tag icon={<ClockCircleOutlined />}>
              Pending
            </Tag>
          </Tooltip>
        )
      ),
      filters: [
        { text: 'Has Response', value: 'replied' },
        { text: 'Pending', value: 'pending' },
      ],
      onFilter: (value, record) => value === 'replied' ? !!record.response : !record.response,
    },
    {
      title: 'Attachments',
      key: 'attachments',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <span style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          {record.screenshot_url && (
            <Tooltip title="View Screenshot">
              <Button 
                type="text" 
                size="small"
                icon={<PictureOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedScreenshotId(record.id);
                  setSelectedTitle(record.title);
                  setScreenshotModalOpen(true);
                }}
              />
            </Tooltip>
          )}
          {record.page_url && (
            <Tooltip title="Open Page">
              <Button 
                type="text" 
                size="small"
                icon={<LinkOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(record.page_url!, '_blank');
                }}
              />
            </Tooltip>
          )}
        </span>
      ),
    },
  ];

  const expandedRowRender = (record: Feedback) => {
    const isEditing = editingId === record.id;
    const canEdit = record.status === 'open';

    return (
      <div style={{ padding: '8px 0' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
          {isEditing ? (
            <div style={{ flex: 1 }}>
              <TextArea
                value={editingDescription}
                onChange={(e) => setEditingDescription(e.target.value)}
                rows={4}
                style={{ marginBottom: 8 }}
                autoFocus
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => handleSaveDescription(record.id)}
                  loading={savingDescription}
                  size="small"
                >
                  Save
                </Button>
                <Button
                  icon={<CloseOutlined />}
                  onClick={handleCancelEdit}
                  disabled={savingDescription}
                  size="small"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Paragraph 
                style={{ 
                  margin: 0,
                  flex: 1,
                  whiteSpace: 'pre-wrap',
                  color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
                }}
              >
                {record.description}
              </Paragraph>
              {canEdit && (
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleStartEdit(record)}
                  size="small"
                  title="Edit description"
                />
              )}
            </>
          )}
        </div>
      
      {record.screenshot_url && (
        <div style={{ marginTop: 16 }}>
          <Button
            type="link"
            icon={<PictureOutlined />}
            onClick={() => {
              setSelectedScreenshotId(record.id);
              setSelectedTitle(record.title);
              setScreenshotModalOpen(true);
            }}
            style={{ padding: 0 }}
          >
            View Screenshot
          </Button>
        </div>
      )}
      
      {record.page_url && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">Page URL: </Text>
          <a href={record.page_url} target="_blank" rel="noopener noreferrer">
            {record.page_url}
          </a>
        </div>
      )}

      {record.user_agent && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">User Agent: </Text>
          <Text code style={{ fontSize: 12, wordBreak: 'break-all' }}>
            {record.user_agent}
          </Text>
        </div>
      )}

        {/* Admin Response */}
        {record.response && (
          <div style={{ 
            marginTop: 16,
            padding: 12,
            background: theme === 'dark' ? '#1a2e1a' : '#f6ffed',
            border: `1px solid ${theme === 'dark' ? '#274916' : '#b7eb8f'}`,
            borderRadius: 6,
          }}>
            <div style={{ marginBottom: 8 }}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
              <Text strong style={{ color: theme === 'dark' ? '#95de64' : '#389e0d' }}>
                Response from {record.response_by}
              </Text>
              {record.response_at && (
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  • {formatDate(record.response_at)}
                </Text>
              )}
            </div>
            <Paragraph 
              style={{ 
                margin: 0,
                whiteSpace: 'pre-wrap',
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
              }}
            >
              {record.response}
            </Paragraph>
          </div>
        )}
      </div>
    );
  };

  // Count by status
  const statusCounts = {
    open: feedback.filter(f => f.status === 'open').length,
    reviewed: feedback.filter(f => f.status === 'reviewed').length,
    planned: feedback.filter(f => f.status === 'planned').length,
    closed: feedback.filter(f => f.status === 'closed').length,
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '8px 8px 4px 8px', flexShrink: 0 }}>
        <Card>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <UserOutlined style={{ fontSize: 16 }} />
                <Text strong style={{ fontSize: 16 }}>My Feedback</Text>
                {authState.user && (
                  <>
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      ({authState.user.name || `${authState.user.firstName} ${authState.user.lastName}`})
                    </Text>
                    {authState.user.teamDisplayName && (
                      <>
                        <TeamOutlined style={{ fontSize: 14, marginLeft: 8 }} />
                        <Tag color="blue" style={{ margin: 0 }}>{authState.user.teamDisplayName}</Tag>
                      </>
                    )}
                  </>
                )}
              </div>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                View and track your submitted bug reports and feature requests
              </Text>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {/* Status summary - clickable toggle buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                <Tag
                  color={statusFilters.has('open') ? 'blue' : 'default'}
                  style={{
                    cursor: 'pointer',
                    opacity: statusFilters.has('open') ? 1 : 0.5,
                    userSelect: 'none',
                  }}
                  onClick={() => toggleStatusFilter('open')}
                >
                  {statusCounts.open} Open
                </Tag>
                <Tag
                  color={statusFilters.has('reviewed') ? 'purple' : 'default'}
                  style={{
                    cursor: 'pointer',
                    opacity: statusFilters.has('reviewed') ? 1 : 0.5,
                    userSelect: 'none',
                  }}
                  onClick={() => toggleStatusFilter('reviewed')}
                >
                  {statusCounts.reviewed} Reviewed
                </Tag>
                <Tag
                  color={statusFilters.has('planned') ? 'cyan' : 'default'}
                  style={{
                    cursor: 'pointer',
                    opacity: statusFilters.has('planned') ? 1 : 0.5,
                    userSelect: 'none',
                  }}
                  onClick={() => toggleStatusFilter('planned')}
                >
                  {statusCounts.planned} Planned
                </Tag>
                <Tag
                  color={statusFilters.has('closed') ? undefined : 'default'}
                  style={{
                    cursor: 'pointer',
                    opacity: statusFilters.has('closed') ? 1 : 0.5,
                    userSelect: 'none',
                  }}
                  onClick={() => toggleStatusFilter('closed')}
                >
                  {statusCounts.closed} Closed
                </Tag>
              </div>

              <Button
                icon={<ReloadOutlined />}
                onClick={fetchFeedback}
                loading={loading}
              />
              
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setFeedbackModalOpen(true)}
              />
            </div>
          </div>

          {error && (
            <Alert
              title="Error"
              description={error}
              type="error"
              closable={{ onClose: () => setError(null) }}
              style={{ marginTop: 16 }}
            />
          )}
        </Card>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 8px 8px' }}>
        {loading ? (
          <Card style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </Card>
        ) : feedback.length === 0 ? (
          <Card>
            <Empty 
              description={
                <span>
                  You haven't submitted any feedback yet.
                  <br />
                  <Button 
                    type="link" 
                    onClick={() => setFeedbackModalOpen(true)}
                    style={{ padding: 0, marginTop: 8 }}
                  >
                    Submit your first bug report or feature request
                  </Button>
                </span>
              }
            />
          </Card>
        ) : (
          <Card>
            <Table
              dataSource={filteredFeedback}
              columns={columns}
              rowKey="id"
              onRow={(record) => ({
                style: getRowStyle(record),
              })}
              expandable={{
                expandedRowRender,
                expandRowByClick: true,
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `${total} feedback item${total !== 1 ? 's' : ''}`,
              }}
              size="middle"
            />
          </Card>
        )}
      </div>

      {/* Feedback Modal */}
      <FeedbackModal 
        open={feedbackModalOpen} 
        onClose={handleFeedbackModalClose} 
      />

      {/* Screenshot Preview Modal */}
      <Modal
        title={`Screenshot: ${selectedTitle}`}
        open={screenshotModalOpen}
        onCancel={() => {
          setScreenshotModalOpen(false);
          setSelectedScreenshotId(null);
          setSelectedTitle('');
        }}
        footer={null}
        width={800}
        centered
      >
        {selectedScreenshotId && (
          <div style={{ textAlign: 'center' }}>
            <Image
              src={getScreenshotUrl(selectedScreenshotId)}
              alt="Feedback screenshot"
              style={{ maxWidth: '100%', borderRadius: 4 }}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

